
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, CheckCircle, ClipboardList, AlertTriangle, Lightbulb } from "lucide-react";

interface GuidanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guidance: {
    guidance_id: string | null;
    requirement_title: string | null;
    intent: string | null;
    implementation_options: string | null;
    documentation_needed: string | null;
    on_site_auditor_visit: string | null;
    standard_requirement: string | null;
  } | null;
  requirementText: string;
}

const GuidanceDialog = ({ open, onOpenChange, guidance, requirementText }: GuidanceDialogProps) => {
  if (!guidance) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Guidance: {guidance.requirement_title || 'Requirement Guidance'}</DialogTitle>
          <DialogDescription>
            Guidance ID: {guidance.guidance_id || 'N/A'}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-120px)] overflow-auto pr-4">
          <div className="space-y-6 pb-4">
            <div className="p-4 bg-muted/50 rounded-md">
              <h3 className="font-medium mb-2 flex items-center text-primary">
                <FileText className="h-4 w-4 mr-2" />
                Requirement
              </h3>
              <p className="text-sm">{requirementText}</p>
            </div>

            {guidance.intent && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-amber-600">
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Intent
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.intent}</p>
              </div>
            )}

            {guidance.standard_requirement && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-blue-600">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Standard Requirement
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.standard_requirement}</p>
              </div>
            )}

            {guidance.implementation_options && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-green-600">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Implementation Options
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.implementation_options}</p>
              </div>
            )}

            {guidance.documentation_needed && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-violet-600">
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Documentation Needed
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.documentation_needed}</p>
              </div>
            )}

            {guidance.on_site_auditor_visit && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-orange-600">
                  <ClipboardList className="h-4 w-4 mr-2" />
                  On-Site Auditor Visit
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.on_site_auditor_visit}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default GuidanceDialog;
